<template>
    <div>
        <ConfirmDialog
            :is-open="updateLocalizationDialog"
            :data="localization"
            :action-loading="updateLocalizationLoading"
            @on-confirm="updateLocalization"
            @on-abort="closeUpdateLocalizationDialog"
        >
            <template v-if="localization.inCampaign" #content>
                Czy na pewno chcesz wyłączyć lokalizację?
            </template>
            <template v-else #content>
                Czy na pewno chcesz włączyć lokalizację?
            </template>
            <template #confirmButton>
                Potwierdź
            </template>
        </ConfirmDialog>
        <OutdoorsCampaignLocalizationScreens
            :is-open="outdoorsLocalizationScreensDialog"
            :localization-data="localization"
            @get-campaign-localization-screens="getLocalizationScreens"
            @get-campaign-localizations="getOutdoorsCampaignLocalizations"
            @on-close="closeOutdoorsLocalizationScreenDialog"
        />
        <v-container class="pa-0">
            <v-row align="center">
                <v-col cols="12" lg="12">
                    <v-container fluid class="pa-0">
                        <v-row dense justify="end">
                            <v-col sm="auto">
                                <v-checkbox
                                    v-model="filters.inCampaign"
                                    label="W kampanii"
                                    hide-details
                                    class="mt-1"
                                    @change="changeFilter"
                                />
                            </v-col>
                            <v-col cols="12" sm="3" lg="3">
                                <v-text-field
                                    v-model="filters.localization__street"
                                    append-icon="mdi-magnify"
                                    label="Szukaj..."
                                    single-line
                                    clearable
                                    dense
                                    hide-details
                                    outlined
                                    @keyup.enter="changeFilter"
                                    @click:append="changeFilter"
                                    @click:clear="clearFilter('localization__street')"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <v-data-table
                        v-if="outdoorsCampaignLocalizations.length > 0 && campaign.id"
                        :headers="headers"
                        :items="outdoorsCampaignLocalizations"
                        :footer-props="footerProps"
                        :server-items-length="pagination.itemsLength"
                        :page.sync="pagination.page"
                        :mobile-breakpoint="0"
                        dense
                        name="campaignLocalizationsData"
                        @pagination="updatePagination"
                    >
                        <template #item.number="{item}">
                            {{ item.localization.number }}
                        </template>
                        <template #item.city="{item}">
                            {{ item.localization.city }}
                        </template>
                        <template #item.street="{item}">
                            {{ item.localization.street }}
                        </template>
                        <template #item.basketLiftRequired="{item}">
                            <v-icon
                                v-if="item.basketLiftRequired"
                                small
                                color="success"
                            >
                                mdi-check
                            </v-icon>
                            <v-icon v-else small color="error">
                                mdi-close
                            </v-icon>
                        </template>
                        <template #item.inCampaign="{item}">
                            <v-icon
                                v-if="item.inCampaign"
                                small
                                color="success"
                            >
                                mdi-check
                            </v-icon>
                            <v-icon v-else small color="error">
                                mdi-close
                            </v-icon>
                        </template>
                        <template #item.isDone="{item}">
                            <v-icon
                                v-if="item.inCampaign && item.isDone"
                                small
                                color="success"
                            >
                                mdi-check
                            </v-icon>
                            <v-icon v-else small color="error">
                                mdi-close
                            </v-icon>
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Otwórz"
                                            v-on="on"
                                            @click="showLocalizationScreensDialog(item)"
                                        >
                                            <v-icon color="primary">
                                                mdi-ballot
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Otwórz</span>
                                </v-tooltip>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            :data-sentry-label="item.inCampaign ? 'Wyłącz' : 'Włącz'"
                                            v-on="on"
                                            @click="showUpdateLocalizationDialog(item)"
                                        >
                                            <v-icon color="pink">
                                                mdi-eye-off
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span v-if="item.inCampaign">Wyłącz</span>
                                    <span v-else>Włącz</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>

                    <v-alert
                        v-else
                        type="info"
                        text
                        border="left"
                    >
                        Brak lokalizacji.
                    </v-alert>
                </v-col>
            </v-row>

            <v-row no-gutters>
                <v-col cols="auto">
                    <v-btn
                        color="primary"
                        outlined
                        class="mt-3 mb-2"
                        data-sentry-label="Poprzedni krok"
                        @click="showCampaignData"
                    >
                        Poprzedni krok
                    </v-btn>
                </v-col>
                <v-row no-gutters justify="end">
                    <v-col cols="auto">
                        <v-btn
                            color="success"
                            class="mt-3 mb-2"
                            :loading="actionLoading"
                            data-sentry-label="Zapisz"
                            @click="saveOutdoorsCampaign"
                        >
                            Zapisz
                        </v-btn>
                    </v-col>
                </v-row>
            </v-row>
        </v-container>
    </div>
</template>

<script>
import { isEmpty } from 'lodash'
import tooltipDelay from '@/mixins/tooltip'
import pagination from '@/mixins/dataTablePagination'
import { errorHandler } from '@/utils/errorHandler'
import filters from '@/mixins/dataTableFilters'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import OutdoorsCampaignLocalizationScreens from './OutdoorsCampaignLocalizationScreens'

export default {
    name: 'OutdoorsCampaignLocalizations',
    components: {
        ConfirmDialog,
        OutdoorsCampaignLocalizationScreens
    },
    mixins: [
        tooltipDelay,
        pagination,
        filters
    ],
    props: {
        actionLoading: {
            type: Boolean
        },
        action: {
            type: String,
            default: null
        },
        campaignData: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            campaign: {},
            localization: {},
            updateLocalizationLoading: false,
            updateLocalizationDialog: false,
            outdoorsLocalizationScreensDialog: false,
            headers: [
                {
                    text: 'Numer',
                    value: 'number',
                    sortable: false
                },
                {
                    text: 'Miasto',
                    value: 'city',
                    sortable: false,
                },
                {
                    text: 'Adres',
                    value: 'street',
                    sortable: false,
                },
                {
                    text: 'Wymagany podnośnik',
                    value: 'basketLiftRequired',
                    sortable: false,
                    align: 'center',
                    width: 120
                },
                {
                    text: 'W kampanii',
                    value: 'inCampaign',
                    sortable: false,
                    align: 'center',
                    width: 120
                },
                {
                    text: 'Zakończona',
                    value: 'isDone',
                    sortable: false,
                    align: 'center',
                    width: 120
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ]
        }
    },
    computed: {
        outdoorsCampaignLocalizations() {
            return this.$store.state.outdoors.outdoorsCampaigns.outdoorsCampaigns.outdoorsCampaignLocalizations
        },
        savedFilters() {
            return this.$store.state.outdoors.outdoorsCampaigns.outdoorsCampaigns.filters
        },
        filterQuery() {
            const filters = Object.assign({}, this.filters)

            filters.campaign_id = this.campaign.id
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        },
    },
    watch: {
        campaignData(value) {
            if (!isEmpty(value)) {
                this.campaign = JSON.parse(JSON.stringify(this.campaignData))
            }
        }
    },
    methods: {
        getOutdoorsCampaignLocalizations() {
            this.$store.dispatch('outdoors/outdoorsCampaigns/getOutdoorsCampaignLocalizations', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        async getOutdoorsCampaignLocalization(localization) {
            return await this.$store.dispatch('outdoors/outdoorsCampaigns/getOutdoorsCampaignLocalization', localization.id)
                .then(response => {
                    this.localization = response.data
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showUpdateLocalizationDialog(localization) {
            this.getOutdoorsCampaignLocalization(localization).then(() => {
                this.updateLocalizationDialog = true
            })
        },
        updateLocalization(localization) {
            this.updateLocalizationLoading = true
            localization.inCampaign = !localization.inCampaign
            this.$store.dispatch('outdoors/outdoorsCampaigns/updateOutdoorsCampaignLocalization', localization)
                .then(() => {
                    this.getOutdoorsCampaignLocalizations()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeUpdateLocalizationDialog()
                })
        },
        closeUpdateLocalizationDialog() {
            this.updateLocalizationLoading = false
            this.updateLocalizationDialog = false

            setTimeout(() => {
                this.localization = {}
            }, 500)
        },
        async getLocalizationScreens() {
            return await this.$store.dispatch('outdoors/outdoorsCampaigns/getOutdoorsCampaignLocalizationScreens', this.localization.id)
                .catch(error => {
                    errorHandler(error)
                })
        },
        showLocalizationScreensDialog(localization) {
            this.getOutdoorsCampaignLocalization(localization).then(() => {
                this.getLocalizationScreens().then(() => {
                    this.outdoorsLocalizationScreensDialog = true
                })
            })
        },
        closeOutdoorsLocalizationScreenDialog() {
            this.outdoorsLocalizationScreensDialog = false
            setTimeout(() => {
                this.localization = {}
            }, 500)
        },
        showCampaignData() {
            this.savePagination(this.$options.name)
            this.$emit('show-campaign-data')
        },
        saveOutdoorsCampaign() {
            this.$emit('save-outdoors-campaign')
        },
        changeFilter() {
            this.$store.dispatch('outdoors/outdoorsCampaigns/setOutdoorsCampaignFilters', this.savedFilters)
            this.resetPagination()
            this.getOutdoorsCampaignLocalizations()
        },
        updatePagination(pagination) {
            this.pagination = pagination
            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending) {
                this.getOutdoorsCampaignLocalizations()
            }
            this.clearPaginationPending()
        }
    }
}
</script>